import { useState, useRef, useEffect } from 'react';
import {
  CardRadioButton,
  Input,
  InputTypes,
  MLPVersion,
  useTranslation,
  NextgenImage,
  AccountType,
  SkeletonFrame,
  InputValidations,
  LoaderType,
  Theme,
} from '@nextgen-web-framework/all';
import { cartApi, AmwayUserDetails, AmwayUserDetailsStatusEnum } from '@commerce-cart-webcomponents/sdk';
import DownlineUserCard from '../DownlineUserCard/DownlineUserCard';
import SubHeader from '../SubHeader/SubHeader';
import NoDownlines from '../../subscription-management/molecules/NoDownlines/NoDownlines';
import { SearchDownlinePropTypes } from './types/propTypes';
import styles from './SearchDownline.module.scss';

const SearchDownline = ({ onDownlineSelect, title, shouldShowRadioSelection = true }: SearchDownlinePropTypes) => {
  const t = useTranslation();

  const [inputText, setInputText] = useState<string>('');
  const [searchIconClick, setSearchIconClick] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [profiles, setProfiles] = useState<AmwayUserDetails[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<AmwayUserDetails>();
  const [isResultFetched, setIsResultFetched] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isInputTextInValid = inputText.length > 0 && inputText.trim().length < 3;

  const handleInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setInputText(searchValue);
    setSearchIconClick(false);
    if (searchValue.length >= 3) {
      // setShowLoader(true);
      getAccounts(searchValue);
    } else {
      setProfiles([]);
      setIsResultFetched(false);
    }
  };

  const onSearchClick = () => {
    setSearchIconClick(true);
    if (!isInputTextInValid) {
      getAccounts();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setSearchIconClick(false);
    if (isInputTextInValid && (('key' in e && e.key === 'Enter') || e.type === 'click')) {
      setIsResultFetched(false);
      setProfiles([]);
      return;
    } else if (!isInputTextInValid && (('key' in e && e.key === 'Enter') || e.type === 'click')) {
      getAccounts();
    }
  };

  const handleSearchIconClick = () => {
    onSearchClick();
    setSearchIconClick(true);
  };

  const handleClearSearch = () => {
    setInputText('');
    setProfiles([]);
    setIsResultFetched(false);
  };

  const getAccounts = (searchStr?: string) => {
    const searchValue = typeof searchStr !== 'undefined' ? searchStr.trim() : inputText.trim();
    if (searchValue.length >= 3) {
      setShowLoader(true);
      cartApi
        .getProfiles({
          cartId: '',
          searchString: searchValue,
          limit: '100',
          offset: '0',
        })
        /* eslint-disable  @typescript-eslint/no-explicit-any */
        .then((res: any) => {
          const responseTransformer = res?.accountSearch?.map(
            (account: {
              accountName: {
                name: string;
              };
              accountNumber: number;
              accountType: AccountType;
              profile: {
                profileId: string;
                displayGivenName: string;
                displayFamilyName: string;
              };
              status: AmwayUserDetailsStatusEnum;
            }) => ({
              amwayUserId: account.profile.profileId,
              accountId: String(account.accountNumber),
              firstName: account.profile.displayGivenName || account.accountName.name,
              lastName: account.profile.displayFamilyName || '',
              accountType: account.accountType,
              status: account.status,
            }),
          );

          setShowLoader(false);
          setIsResultFetched(true);
          setProfiles(responseTransformer);
          setSelectedProfile(responseTransformer[0]);
          shouldShowRadioSelection &&
            onDownlineSelect({
              ...(responseTransformer[0]?.status !== AmwayUserDetailsStatusEnum.Inactive
                ? responseTransformer[0]
                : { amwayUserId: '' }),
            });
        })
        .catch(() => {
          setIsResultFetched(true);
          setProfiles([]);
          setShowLoader(false);
          shouldShowRadioSelection && onDownlineSelect({ amwayUserId: '' });
        });
    }
  };

  const handleProfileSelection = (profileDetails: AmwayUserDetails) => {
    if (profileDetails.status === AmwayUserDetailsStatusEnum.Inactive) {
      return;
    }
    setSelectedProfile(profileDetails);
    onDownlineSelect(profileDetails);
  };

  const renderAccounts = () => {
    if (isResultFetched && profiles.length > 0) {
      return (
        <div className={styles['account-name-container-results']} data-testid="account-test-result">
          <SubHeader headingText={t('subscription.manualsop.accounts.list.title', { count: `${profiles.length}` })} />

          <div className={styles['account-name-container-results-wrapper']}>
            {profiles.map((profile) => {
              const { amwayUserId, accountId, firstName, lastName, status } = profile;
              const checkedClass = selectedProfile?.amwayUserId === amwayUserId ? styles['result-checked'] : '';
              const disableCard = status === AmwayUserDetailsStatusEnum.Inactive;

              return (
                <div className={checkedClass} key={accountId}>
                  {shouldShowRadioSelection ? (
                    <>
                      <CardRadioButton
                        checked={selectedProfile?.amwayUserId === amwayUserId}
                        mlpVersion={MLPVersion.ONE}
                        label={`${firstName} ${lastName}`}
                        name="account-number-or-name"
                        cardDescription={`${t('cart.cartEllipsesMenu.accountNum')} ${accountId}`}
                        disabled={disableCard}
                        onChange={() => handleProfileSelection(profile)}
                        data-testid="search-result-item"
                        theme={disableCard ? Theme.BLACK : Theme.PURPLE}
                      />
                    </>
                  ) : (
                    <DownlineUserCard profile={profile} onDownlineUserSelect={onDownlineSelect} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (isResultFetched && inputText && profiles.length === 0) {
      return (
        <NoDownlines headingText={t('subscription.manualsop.accounts.list.title', { count: `${profiles.length}` })} />
      );
    }

    if (isInputTextInValid) {
      return (
        <div className={styles['search-suggestion-container']} data-testid="search-suggestion-testid">
          <NextgenImage
            alt={t('subscription.subscriptionmanagement.tabs.downline.searchDownliesText')}
            src="/icons/search.svg"
            data-testid="no-result-image-testid"
            objectFit="contain"
            width={71}
            height={96}
            className={styles['search-image']}
            isRelativeURL={true}
            loaderType={LoaderType.WEBFONTS}
          />
          <div className={styles['suggestion-content']}>
            <div className={styles['content-title']}>
              {t('subscription.subscriptionmanagement.tabs.downline.searchDownliesText')}
            </div>
            <div className={styles['content-subtitle']}>
              {t('subscription.subscriptionmanagement.tabs.downline.searchSuggestionText')}
            </div>
          </div>
        </div>
      );
    }

    return <></>;
  };

  const ProfileLoader = () => (
    <div className={styles['profile-loader']}>
      <SkeletonFrame isRoundCorner={true} heightValue="28" widthValue="200" borderRadiusValue="12" />
      <div className={styles['card']}>
        <SkeletonFrame className={styles['card-width']} isRoundCorner={true} heightValue="60" borderRadiusValue="12" />
      </div>
    </div>
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className={styles['subscription-type-container']}>
      {title && <label className={styles['subscription-type-title']}>{title}</label>}
      <div className={styles['account-name-container']}>
        <div className={styles['account-name-search']}>
          <Input
            ref={inputRef}
            type={InputTypes.TEXT}
            label={t('subscription.manualsop.input.accountname.placeholder')}
            mlpVersion={MLPVersion.TWO}
            value={inputText}
            onChange={handleInputChanged}
            onKeyDown={(e) => {
              onKeyDown(e);
            }}
            name="account-name-or-number"
            className={styles['account-name-search-input']}
            required={true}
            data-testid="search-input"
            inputIcon={{
              iconName: searchIconClick ? 'close' : 'search',
            }}
            iconClassName={styles['account-name-search-icon']}
            inputIconOnClick={searchIconClick ? handleClearSearch : handleSearchIconClick}
            status={isInputTextInValid ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={isInputTextInValid ? t('subscription.bownline.search.enterAtleast3Char') : ''}
          />
        </div>
        {showLoader ? <ProfileLoader /> : renderAccounts()}
      </div>
    </div>
  );
};

export default SearchDownline;
